import React, {
    createContext,
    useContext,
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback,
} from "react";
import { CheckoutType, ICartItemData, ICheckoutContext } from "@interfaces";
import { useDispatch } from "react-redux";
import { AppDispatch, selectAccount, useAppSelector, useIsLoggedIn } from "@redux";
import { fetchActiveDiscount } from "./checkout";
import _ from "lodash";
import { getItemsPriceInfo, transformCartItemToAddItemBase } from "./checkoutHelpers";
import { discountApi } from "src/Api/discountApi";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { WarnToast } from "src/Components/toasts/WarnToast";
import { sessionHelper } from "@utilities";

interface CheckoutContextType {
    checkoutContext: ICheckoutContext;
    items: ICartItemData[];
    setItems: React.Dispatch<React.SetStateAction<ICartItemData[]>>;
    initialItems: ICartItemData[];
    isOrderDiscountLoading: boolean;
    activeDiscount?: number;
    orderDiscount?: number;
    originalPrice: number;
    discountedPrice?: number;
    pointsAvailable?: number;
    verifyPoints: () => Promise<void>;
    isVerifyingPoints: boolean;
    /**
     * True while the traveller form is turning the account into a points agent.
     * The Order Summary reads this so it shows its loading state instead of the
     * pre-upgrade totals, which would otherwise sit there looking final for the
     * second or two the upgrade and the account reload take.
     */
    isUpgradingPoints: boolean;
    setIsUpgradingPoints: (value: boolean) => void;
}

export const CheckoutContext = createContext<CheckoutContextType | undefined>(undefined);

export const useCheckout = () => {
    const ctx = useContext(CheckoutContext);
    if (!ctx) throw new Error("useCheckout must be used within a CheckoutProvider");
    return ctx;
};

export const CheckoutProvider: React.FC<{
    initialItems: ICartItemData[];
    activeDiscount?: number;
    checkoutContext: ICheckoutContext;
    sessionId?: string | null;
    children: React.ReactNode;
}> = ({
    initialItems: initialItemsProp,
    activeDiscount,
    checkoutContext,
    sessionId: sessionIdProp,
    children,
}) => {
    const [items, setItems] = useState<ICartItemData[]>(initialItemsProp);
    const initialItems = useRef(initialItemsProp).current;

    // Sync items when initialItemsProp changes from backend
    useEffect(() => {
        setItems(initialItemsProp);
    }, [initialItemsProp]);

    const dispatch = useDispatch<AppDispatch>();
    const [orderDiscount, setOrderDiscount] = useState<number | undefined>();
    const [pointsAvailable, setPointsAvailable] = useState<number | undefined>();
    const [isOrderDiscountLoading, setIsOrderDiscountLoading] = useState(false);
    const [isVerifyingPoints, setIsVerifyingPoints] = useState(false);
    const [isUpgradingPoints, setIsUpgradingPoints] = useState(false);
    const { account, metadata } = useAppSelector(selectAccount);
    const isLoggedIn = useIsLoggedIn();
    const isPointsAgent = metadata?.is_points_agent ?? false;
    const isCommissionAgent = metadata?.is_commission_agent ?? false;
    const sessionId = useMemo(() => {
        if (sessionIdProp !== undefined) return sessionIdProp;
        const urlParams = new URLSearchParams(window.location.search);
        const urlSessionId = urlParams.get("sessionId");
        if (urlSessionId) return urlSessionId;
        if (!isLoggedIn && sessionHelper.hasSession()) return sessionHelper.getSessionId();
        return null;
    }, [isLoggedIn, sessionIdProp]);

    const originalPrice = useMemo(() => {
        return getItemsPriceInfo(items).totalPrice;
    }, [items]);

    const discountedPrice = useMemo(() => {
        return (orderDiscount ?? 0) > 0
            ? originalPrice - (originalPrice * (orderDiscount ?? 0)) / 100
            : undefined;
    }, [orderDiscount, originalPrice]);

    useEffect(() => {
        if (!activeDiscount && !isPointsAgent && !isCommissionAgent) {
            dispatch(fetchActiveDiscount());
        }
    }, [activeDiscount, isPointsAgent, isCommissionAgent, dispatch]);

    const itemsKey = useMemo(() => JSON.stringify(items), [items]);

    useEffect(() => {
        // Don't fetch discount if cart is empty
        if (items.length === 0) {
            setIsOrderDiscountLoading(false);
            return;
        }

        // Skip if no active discount and user is not a points/commission agent
        if ((!activeDiscount || activeDiscount === 0) && !isPointsAgent && !isCommissionAgent) {
            setIsOrderDiscountLoading(false);
            return;
        }

        const fetchDiscount = async () => {
            setIsOrderDiscountLoading(true);

            // Declare variables outside try block for error logging
            const itemsChanged = !_.isEqual(initialItems, items);
            const isBookNow = checkoutContext.checkoutType === CheckoutType.BOOK_NOW;
            const needsDryRun = itemsChanged || isBookNow || !isLoggedIn || !!sessionId;

            try {
                let resp;
                // Dry run is needed when we can't fetch discount from saved cart/quote:
                // 1. No account - guest user without saved data
                // 2. Items changed - local modifications not yet saved to backend
                // 3. Book now flow - no saved cart/quote exists
                // 4. Not logged in - session-based users without persisted data

                // For logged-in users with unchanged items, prefer getCartDiscount/getQuoteDiscount
                // Only use dry run if items changed, book now flow, not logged in, or has URL sessionId

                if (needsDryRun) {
                    const updatedItems = transformCartItemToAddItemBase(items);
                    if (sessionId) {
                        resp = await discountApi.checkOrderDiscount(updatedItems, sessionId);
                    } else {
                        resp = await discountApi.checkOrderDiscount(updatedItems);
                    }
                } else if (checkoutContext.checkoutType === CheckoutType.CART) {
                    resp = await discountApi.getCartDiscount();
                } else if (checkoutContext.checkoutType === CheckoutType.QUOTE) {
                    resp = await discountApi.getQuoteDiscount(checkoutContext.quoteId!);
                } else {
                    // optional: handle unexpected type
                    return;
                }

                setOrderDiscount(resp.data.data.applicableDiscount);
                setPointsAvailable(resp.data.data.pointsAvailable);
            } catch (err: any) {
                const errorMessage =
                    err?.response?.data?.title || err?.message || "Failed to load order discount";
                ErrorToast({ Message: errorMessage });
                setPointsAvailable(0);
            } finally {
                setIsOrderDiscountLoading(false);
            }
        };

        fetchDiscount();
    }, [
        account?.is_agent_integrated,
        activeDiscount,
        isPointsAgent,
        isCommissionAgent,
        checkoutContext.checkoutType,
        checkoutContext.quoteId,
        isLoggedIn,
        sessionId,
        itemsKey,
    ]);

    const verifyPoints = useCallback(async () => {
        // The account slice is persisted and outlives the JWT, so a signed-out
        // visitor can still be carrying is_agent_integrated from an earlier
        // session. Verifying points without a token answers 401.
        if (!isLoggedIn) return;
        if (!account?.is_agent_integrated) return;
        if (pointsAvailable === undefined || pointsAvailable === null) return;
        if (checkoutContext.checkoutType === CheckoutType.QUOTE && !checkoutContext.quoteId) return;

        setIsVerifyingPoints(true);
        try {
            let resp;
            if (checkoutContext.checkoutType === CheckoutType.QUOTE && checkoutContext.quoteId) {
                resp = await discountApi.verifyQuotePoints(
                    checkoutContext.quoteId,
                    pointsAvailable,
                );
            } else {
                resp = await discountApi.verifyCartPoints(pointsAvailable);
            }

            const verifyData = resp.data.data;
            if (verifyData.pointsChanged) {
                setPointsAvailable(verifyData.pointsAvailable);
                WarnToast({ Message: "Your rewards points have been updated" });
            }
        } catch (err: any) {
            console.error("Failed to verify points:", err);
        } finally {
            setIsVerifyingPoints(false);
        }
    }, [
        isLoggedIn,
        account?.is_agent_integrated,
        pointsAvailable,
        checkoutContext.checkoutType,
        checkoutContext.quoteId,
    ]);

    return (
        <CheckoutContext.Provider
            value={{
                items,
                setItems,
                initialItems,
                checkoutContext,
                isOrderDiscountLoading,
                orderDiscount,
                discountedPrice,
                originalPrice,
                activeDiscount,
                pointsAvailable,
                verifyPoints,
                isVerifyingPoints,
                isUpgradingPoints,
                setIsUpgradingPoints,
            }}
        >
            {children}
        </CheckoutContext.Provider>
    );
};
